import React, { useState, useCallback } from 'react';
import { message } from 'antd';

const SignUpsV2Controller = ({
  children,
  campaignOrderId,
  campaignOrder,
  signupsData,
  signupsLoading,
  signupsByDateData,
  chartLoading,
  getSignupsPaginated,
  getSignupsByDate,
  setSignupsV2Filters,
  ...props
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dateRange, setDateRange] = useState([null, null]);
  const [attrMethod, setAttrMethod] = useState(null);
  const [attrWindow, setAttrWindow] = useState(30);

  // Handle date range change
  const handleDateRangeChange = useCallback(
    (dates) => {
      setDateRange(dates);
      setCurrentPage(1);

      const startDate = dates && dates[0] ? dates[0].format('YYYY-MM-DD') : null;
      const endDate = dates && dates[1] ? dates[1].format('YYYY-MM-DD') : null;

      // Update parent state for export buttons
      if (setSignupsV2Filters) {
        setSignupsV2Filters((prev) => ({
          ...prev,
          dateRange: dates,
        }));
      }

      getSignupsPaginated({
        variables: {
          campaignOrderId,
          skip: 0,
          first: pageSize,
          startDate,
          endDate,
          attrMethod,
          attrWindow,
        },
      });

      // Fetch updated chart data
      getSignupsByDate({
        variables: {
          campaignOrderId,
          startDate,
          endDate,
        },
      });
    },
    [campaignOrderId, pageSize, attrMethod, attrWindow, getSignupsPaginated, getSignupsByDate, setSignupsV2Filters]
  );

  // Handle attribution method change
  const handleAttrMethodChange = useCallback(
    (method) => {
      setAttrMethod(method);
      setCurrentPage(1);

      // Update parent state for export buttons
      if (setSignupsV2Filters) {
        setSignupsV2Filters((prev) => ({
          ...prev,
          attrMethod: method,
        }));
      }

      const startDate = dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : null;
      const endDate = dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : null;

      getSignupsPaginated({
        variables: {
          campaignOrderId,
          skip: 0,
          first: pageSize,
          startDate,
          endDate,
          attrMethod: method,
          attrWindow,
        },
      });
    },
    [campaignOrderId, pageSize, dateRange, attrWindow, getSignupsPaginated, setSignupsV2Filters]
  );

  // Handle attribution window change
  const handleAttrWindowChange = useCallback(
    (window) => {
      setAttrWindow(window);
      setCurrentPage(1);

      // Update parent state for export buttons
      if (setSignupsV2Filters) {
        setSignupsV2Filters((prev) => ({
          ...prev,
          attrWindow: window,
        }));
      }

      const startDate = dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : null;
      const endDate = dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : null;

      getSignupsPaginated({
        variables: {
          campaignOrderId,
          skip: 0,
          first: pageSize,
          startDate,
          endDate,
          attrMethod,
          attrWindow: window,
        },
      });
    },
    [campaignOrderId, pageSize, dateRange, attrMethod, getSignupsPaginated, setSignupsV2Filters]
  );

  // Handle pagination change
  const handlePageChange = useCallback(
    (page, size) => {
      setCurrentPage(page);
      setPageSize(size);

      const startDate = dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : null;
      const endDate = dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : null;

      getSignupsPaginated({
        variables: {
          campaignOrderId,
          skip: (page - 1) * size,
          first: size,
          startDate,
          endDate,
          attrMethod,
          attrWindow,
        },
      });
    },
    [campaignOrderId, dateRange, attrMethod, attrWindow, getSignupsPaginated]
  );

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (page, size) => {
      setCurrentPage(1);
      setPageSize(size);

      const startDate = dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : null;
      const endDate = dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : null;

      getSignupsPaginated({
        variables: {
          campaignOrderId,
          skip: 0,
          first: size,
          startDate,
          endDate,
          attrMethod,
          attrWindow,
        },
      });
    },
    [campaignOrderId, dateRange, attrMethod, attrWindow, getSignupsPaginated]
  );

  // Create view props
  const viewProps = {
    campaignOrderId,
    campaignOrder,
    signupsData,
    signupsLoading,
    signupsByDateData,
    chartLoading,
    onDateRangeChange: handleDateRangeChange,
    onAttrMethodChange: handleAttrMethodChange,
    onAttrWindowChange: handleAttrWindowChange,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    currentPage,
    pageSize,
    dateRange,
    attrMethod,
    attrWindow,
  };

  return React.cloneElement(children, viewProps);
};

export default SignUpsV2Controller;
